import type { FeatureRequest } from './types';

const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'by',
  'for',
  'from',
  'in',
  'into',
  'is',
  'it',
  'of',
  'on',
  'or',
  'our',
  'that',
  'the',
  'their',
  'this',
  'to',
  'we',
  'with',
]);

export interface SimilarFeatureMatch {
  request: FeatureRequest;
  score: number;
}

export interface FeatureCluster {
  id: string;
  canonicalRequest: FeatureRequest;
  requests: FeatureRequest[];
  totalVotes: number;
  newestRequestAt: string;
}

function normalizeText(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stemToken(token: string): string {
  if (token.endsWith('ies') && token.length > 4) {
    return `${token.slice(0, -3)}y`;
  }

  if (token.endsWith('ing') && token.length > 5) {
    return token.slice(0, -3);
  }

  if (token.endsWith('ed') && token.length > 4) {
    return token.slice(0, -2);
  }

  if (token.endsWith('s') && token.length > 3) {
    return token.slice(0, -1);
  }

  return token;
}

export function tokenizeText(input: string): string[] {
  return normalizeText(input)
    .split(' ')
    .map(stemToken)
    .filter((token) => token.length > 2)
    .filter((token) => !STOP_WORDS.has(token));
}

function toTrigrams(input: string): Set<string> {
  const compact = normalizeText(input).replace(/\s+/g, '');
  if (compact.length < 3) {
    return new Set();
  }

  const grams = new Set<string>();
  for (let i = 0; i <= compact.length - 3; i += 1) {
    grams.add(compact.slice(i, i + 3));
  }
  return grams;
}

function toTokenSet(input: string): Set<string> {
  return new Set(tokenizeText(input));
}

function jaccardSimilarity(
  left: Set<string>,
  right: Set<string>,
): number {
  if (left.size === 0 || right.size === 0) {
    return 0;
  }

  let intersection = 0;
  left.forEach((value) => {
    if (right.has(value)) {
      intersection += 1;
    }
  });

  const union = left.size + right.size - intersection;
  if (union === 0) {
    return 0;
  }

  return intersection / union;
}

function textSimilarity(left: string, right: string): number {
  const tokenScore = jaccardSimilarity(toTokenSet(left), toTokenSet(right));
  const trigramScore = jaccardSimilarity(toTrigrams(left), toTrigrams(right));

  return 0.8 * tokenScore + 0.2 * trigramScore;
}

function compareByVotesThenFreshness(
  left: FeatureRequest,
  right: FeatureRequest,
): number {
  if (right.votes.length !== left.votes.length) {
    return right.votes.length - left.votes.length;
  }

  return (
    new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  );
}

export function calculateFeatureSimilarity(
  left: FeatureRequest,
  right: FeatureRequest,
): number {
  const titleScore = textSimilarity(left.title, right.title);
  const descriptionScore = textSimilarity(left.description, right.description);
  const categoryScore = left.category === right.category ? 1 : 0;

  return 0.66 * titleScore + 0.24 * descriptionScore + 0.1 * categoryScore;
}

export function findSimilarFeatureRequests(
  candidate: FeatureRequest,
  requests: FeatureRequest[],
  minScore = 0.2,
  maxResults = 3,
): SimilarFeatureMatch[] {
  return requests
    .filter((request) => request.id !== candidate.id)
    .map((request) => ({
      request,
      score: calculateFeatureSimilarity(candidate, request),
    }))
    .filter((match) => match.score >= minScore)
    .sort((left, right) => right.score - left.score)
    .slice(0, maxResults);
}

class UnionFind {
  private parent = new Map<string, string>();

  private rank = new Map<string, number>();

  add(value: string) {
    if (!this.parent.has(value)) {
      this.parent.set(value, value);
      this.rank.set(value, 0);
    }
  }

  find(value: string): string {
    const parent = this.parent.get(value);
    if (!parent || parent === value) {
      return value;
    }

    const root = this.find(parent);
    this.parent.set(value, root);
    return root;
  }

  union(left: string, right: string) {
    const leftRoot = this.find(left);
    const rightRoot = this.find(right);

    if (leftRoot === rightRoot) {
      return;
    }

    const leftRank = this.rank.get(leftRoot) ?? 0;
    const rightRank = this.rank.get(rightRoot) ?? 0;

    if (leftRank < rightRank) {
      this.parent.set(leftRoot, rightRoot);
      return;
    }

    if (leftRank > rightRank) {
      this.parent.set(rightRoot, leftRoot);
      return;
    }

    this.parent.set(rightRoot, leftRoot);
    this.rank.set(leftRoot, leftRank + 1);
  }
}

export function buildFeatureClusters(
  requests: FeatureRequest[],
  threshold = 0.35,
): FeatureCluster[] {
  if (requests.length === 0) {
    return [];
  }

  const unionFind = new UnionFind();
  requests.forEach((request) => unionFind.add(request.id));

  for (let i = 0; i < requests.length; i += 1) {
    const left = requests[i];
    for (let j = i + 1; j < requests.length; j += 1) {
      const right = requests[j];
      if (calculateFeatureSimilarity(left, right) >= threshold) {
        unionFind.union(left.id, right.id);
      }
    }
  }

  const groups = new Map<string, FeatureRequest[]>();

  requests.forEach((request) => {
    const root = unionFind.find(request.id);
    const group = groups.get(root);

    if (group) {
      group.push(request);
      return;
    }

    groups.set(root, [request]);
  });

  return Array.from(groups.entries())
    .map(([id, groupRequests]) => {
      const sortedRequests = [...groupRequests].sort(compareByVotesThenFreshness);
      const totalVotes = sortedRequests.reduce(
        (sum, request) => sum + request.votes.length,
        0,
      );
      const newestRequestAt = sortedRequests.reduce(
        (latest, request) =>
          new Date(request.createdAt).getTime() > new Date(latest).getTime()
            ? request.createdAt
            : latest,
        sortedRequests[0].createdAt,
      );

      return {
        id,
        canonicalRequest: sortedRequests[0],
        requests: sortedRequests,
        totalVotes,
        newestRequestAt,
      };
    })
    .sort((left, right) => {
      if (right.totalVotes !== left.totalVotes) {
        return right.totalVotes - left.totalVotes;
      }

      return (
        new Date(right.newestRequestAt).getTime() -
        new Date(left.newestRequestAt).getTime()
      );
    });
}

export function getMomentumScore(
  request: FeatureRequest,
  now: Date = new Date(),
): number {
  const ageInHours = Math.max(
    1,
    (now.getTime() - new Date(request.createdAt).getTime()) / (1000 * 60 * 60),
  );

  return request.votes.length / ageInHours**0.35;
}
